import { onScopeDispose, Ref, watch } from 'vue';
import { useEventListener, useResizeObserver } from '@vueuse/core';

/**
 * Fixed bottom elements that toasts must stay above (the update prompt, a
 * selection bar). While any is shown, `--app-toast-bottom` on `:root` puts
 * the toasts 12 px above the highest one; with none, the toasts use their
 * default offsets.
 */
const obstacles = new Map<symbol, number>();

/** Space between an obstacle's top edge and the toasts (px). */
const TOAST_GAP = 12;

function applyToastOffset() {
  const root = document.documentElement;
  const highest = Math.max(0, ...obstacles.values());
  if (highest > 0)
    root.style.setProperty('--app-toast-bottom', `${highest + TOAST_GAP}px`);
  else root.style.removeProperty('--app-toast-bottom');
}

/** Registers `element` (while it is rendered) as something toasts avoid. */
export function useToastObstacle(element: Readonly<Ref<HTMLElement | null>>) {
  const key = Symbol('toast-obstacle');

  function measure() {
    const node = element.value;
    const top = node?.getBoundingClientRect().top;
    // Hidden (`display: none`) or gone: it no longer pushes the toasts.
    if (!node || top === undefined || !node.getClientRects().length)
      obstacles.delete(key);
    else obstacles.set(key, Math.max(0, window.innerHeight - top));
    applyToastOffset();
  }

  watch(element, measure, { flush: 'post' });
  useResizeObserver(element, measure);
  useEventListener(window, 'resize', measure);

  onScopeDispose(() => {
    obstacles.delete(key);
    applyToastOffset();
  });

  return { measure };
}
